import SearchFlatButton from './lib/SearchFlatButton/SearchFlatButton';
import { FirstVisitPopup } from '../FirstVisitPopup/FirstVisitPopup';

import { useEffect, useState, useRef } from 'react';
import { attachFlatClickEvent, attachFlatHoverEvent } from './lib/events/FlatEvents';
import { useSVGContext } from '../../contexts/GraphicContext';
import { useFlatsDataContext } from '../../contexts/FlatsDataContext';
import { updateFilterOutFlats, updateFlatColorByChangingSelectedClass, updateFlatColorByStatuses, updateHoveredFlat } from './lib/helpers/svgFlatColorManipulations';
import LoadingSpinner from '../shared/LoadingSpinner/LoadingSpinner';

import styles from './InteractiveGraphicView.module.css';

const InteractiveGraphicView: React.FC = (): JSX.Element => {
    const svgRef = useRef<HTMLObjectElement>(null);
    const [showFirstVisitPopup, setShowFirstVisitPopup] = useState(true);
    const { currentSVG, handleContextFloorClick, handleContextFlatGraphicClick } = useSVGContext();
    const { contextSelectedFlat, contextFlats, contextSelectedFloor, contextHoveredFlat, contextShowSidebar, contextFetchedFlats, setContextSetShowSidebar, setContextHoveredFlat } = useFlatsDataContext();

    const handlePopupClose = () => {
        setShowFirstVisitPopup(false);
    };

    useEffect(() => {
        function handleResize() {
            if (window.innerWidth < 768 && contextFetchedFlats && contextSelectedFloor === '' && !contextShowSidebar) {
                setContextSetShowSidebar(true);
            }
        }

        handleResize();

        window.addEventListener('resize', handleResize);
        
        return () => window.removeEventListener('resize', handleResize);
    }, [contextFetchedFlats, contextSelectedFloor, contextShowSidebar, setContextSetShowSidebar]);

    useEffect(() => {
        const svgObject = svgRef.current;

        if (svgObject) {
            const onLoad = () => {
                const svgDocument = svgObject.contentDocument;

                if (svgDocument) {
                    const floorElements = svgDocument.querySelectorAll('[data-type="floor"]');
                    const flatsElements = svgDocument.querySelectorAll('[data-type="flat"]');

                    attachFloorClickEvent(floorElements);
                    attachFlatClickEvent(flatsElements, handleContextFlatGraphicClick);
                    attachFlatHoverEvent(flatsElements, contextFlats, setContextHoveredFlat)
                    
                    updateFlatColorByChangingSelectedClass(flatsElements, contextSelectedFlat);
                    updateFlatColorByStatuses(svgDocument, contextFlats);
                    updateFilterOutFlats(contextFlats, flatsElements);
                }
            };

            svgObject.addEventListener('load', onLoad);

            return () => {
                svgObject.removeEventListener('load', onLoad);
            };
        }
    }, [currentSVG, handleContextFloorClick]);

    useEffect(() => {
        const svgObject = svgRef.current;

        if (svgObject) {
            const svgDocument = svgObject.contentDocument;

            if (svgDocument) {
                const flatsElements = svgDocument.querySelectorAll('[data-type="flat"]');
                updateFlatColorByChangingSelectedClass(flatsElements, contextSelectedFlat);
            }
        }
    }, [contextSelectedFlat]);

    useEffect(() => {
        const svgObject = svgRef.current;
        const svgDocument = svgObject?.contentDocument;

        updateHoveredFlat(svgDocument, contextHoveredFlat.name, setContextHoveredFlat);
    }, [contextHoveredFlat]);

    useEffect(() => {
        const svgObject = svgRef.current;

        if (svgObject) {
            const svgDocument = svgObject.contentDocument;

            if (svgDocument) {
                const flatsElements = svgDocument.querySelectorAll('[data-type="flat"]');

                updateFlatColorByStatuses(svgDocument, contextFlats);
                updateFilterOutFlats(contextFlats, flatsElements);
            }
        }
    }, [contextFlats]);

    /**
     * Attaches click event listeners to SVG Floor elements.
     * When clicked, retrieves the Floor's ID attribute and invokes context functions to update the selected floor * and show the sidebar.
     * @param {NodeListOf<Element>} floorElements - A list of SVG Floor elements to attach click events to.
     * @returns {void}
    */
    const attachFloorClickEvent = (floorElements: NodeListOf<Element>): void => {
        floorElements.forEach((element: any) => {
            const svgElement = element as SVGElement;
        
            svgElement.addEventListener('click', () => {
              const floorName = svgElement.getAttribute('id'); 
              if (floorName) {
                handleContextFloorClick(floorName);
                setContextSetShowSidebar(true);
              }
            });
        });
    }

    return (
        <>
            {showFirstVisitPopup && contextFetchedFlats && !contextSelectedFloor && (
                <FirstVisitPopup onClose={handlePopupClose} />
            )}
            { (!contextShowSidebar && contextSelectedFloor === '' && contextFetchedFlats) && <SearchFlatButton onHandleClick={() => setContextSetShowSidebar(true)} /> }
            { contextFetchedFlats ? <object
                ref={svgRef}
                data={currentSVG}
                width="100%"
                height="100vh"
                style={{ minHeight: '100vh', maxHeight: '100vh', width: '100vw', display: 'block' }}
                className={`${styles.svgObject} ${contextSelectedFloor || contextSelectedFlat.name ? 'flats_svg' : ''}`}
                type="image/svg+xml"
                id="interactive-graphic"
                aria-label="Interactive Graphic"
            /> : <>
                <LoadingSpinner />
             </> }
        </>
    );
};

export default InteractiveGraphicView;